import { FC, useMemo, useCallback, MouseEvent } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSetAtom } from 'jotai';
import { useIntentsQuery, useSetCommentIdToFocusOn } from '@tymely/services';
import { selectedPolicySetIdAtom } from '@tymely/atoms';
import { routes } from '@tymely/config';

import CommentIdWithTooltip from './components/CommentIdWithTooltip';
import { ToolCallHeaderProps } from './types';
import ThinkingBubbleToggleButton from './components/ThinkingBubbleToggleButton';

const ToolCallHeader: FC<ToolCallHeaderProps> = ({ eventData, setShowThinking, organizationId }) => {
    const intents = useIntentsQuery();
    const setSelectedPolicySetId = useSetAtom(selectedPolicySetIdAtom);
    const setCommentIdToFocusOn = useSetCommentIdToFocusOn();
    const thinkingPart = eventData?.audit_log?.thinking;
    const toolName = eventData?.audit_log?.tool_name;
    const intentId = eventData?.audit_log?.policy_set_id;
    const commentId = eventData?.audit_log?.comment_id;
    const policySetName = useMemo(() => {
        if (intentId) {
            const intent = intents.data?.find((i) => i.id === intentId);
            return intent?.name;
        }
        return null;
    }, [intentId, intents.data]);

    const handleClick = useCallback(() => {
        if (intentId) setSelectedPolicySetId(intentId);
        if (commentId) setCommentIdToFocusOn(commentId);
    }, [intentId, commentId, setSelectedPolicySetId, setCommentIdToFocusOn]);

    const policyEditorUrl =
        organizationId && intentId
            ? commentId != null
                ? `${routes.graphPolicyEditor}/${organizationId}/${intentId}?commentId=${commentId}`
                : `${routes.graphPolicyEditor}/${organizationId}/${intentId}`
            : null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'inline', mr: 1 }}>
                Tool call:
            </Typography>
            <Typography
                onClick={handleClick}
                variant="subtitle2"
                fontWeight="bold"
                component="span"
                sx={{
                    ml: 1,
                    textDecoration: intentId ? 'underline' : 'none',
                    cursor: intentId ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 600,
                    display: 'inline-block',
                    verticalAlign: 'bottom',
                }}
            >
                {`${intentId || toolName} ${policySetName ? `: ${policySetName}` : ''}`}
            </Typography>
            {policyEditorUrl && (
                <IconButton
                    type="button"
                    onClick={(e: MouseEvent) => {
                        e.stopPropagation();
                        window.open(policyEditorUrl, '_blank', 'noopener,noreferrer');
                    }}
                    size="small"
                    sx={{ ml: 0.5, p: 0.25 }}
                    data-qa="toolCallHeaderOpenPolicyLink"
                    aria-label="Open policy in graph editor"
                >
                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
            )}
            {thinkingPart && (
                <ThinkingBubbleToggleButton
                    onClick={(e: MouseEvent) => {
                        e.stopPropagation();
                        setShowThinking((prev) => !prev);
                    }}
                />
            )}
            {commentId && <CommentIdWithTooltip commentId={commentId} />}
        </Box>
    );
};

export default ToolCallHeader;
